/**
 * Estado global (Redux) dos estabelecimentos, geradores, estados e materiais que o usuário tem acesso.
 * Preenchido ao carregar a app (usuário autenticado): geradores/estabelecimentos/estados vêm de
 * GET /generators/me da musa-generator-api e o catálogo de materiais do musa-agreements.
 * Usado nos filtros de todo o portal (estabelecimento, gerador, estado, material).
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchGeneratorsAndEstablishments } from '@/integrations/generatorApi/client';
import { fetchRegistryMaterials } from '@/integrations/agreements/client';
import type {
  GeneratorOption,
  EstablishmentOption,
  StateOption,
  MaterialOption,
} from '@/integrations/generatorApi/client';

export interface EstablishmentAccessState {
  establishments: EstablishmentOption[];
  generators: GeneratorOption[];
  states: StateOption[];
  materials: MaterialOption[];
  loading: boolean;
  error: string | null;
  fetchedAt: number | null;
}

const initialState: EstablishmentAccessState = {
  establishments: [],
  generators: [],
  states: [],
  materials: [],
  loading: false,
  error: null,
  fetchedAt: null,
};

export const fetchEstablishmentsForUser = createAsyncThunk(
  'establishmentAccess/fetch',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchGeneratorsAndEstablishments();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Erro ao carregar filtros';
      return rejectWithValue(message);
    }
  }
);

export const fetchMaterialsForUser = createAsyncThunk(
  'establishmentAccess/fetchMaterials',
  async (_, { rejectWithValue }) => {
    try {
      const result = await fetchRegistryMaterials();
      return result.map((m) => ({ id: String(m.id), name: m.name }));
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Erro ao carregar materiais';
      return rejectWithValue(message);
    }
  }
);

const establishmentAccessSlice = createSlice({
  name: 'establishmentAccess',
  initialState,
  reducers: {
    clearEstablishmentAccess: (state) => {
      state.establishments = [];
      state.generators = [];
      state.states = [];
      state.materials = [];
      state.error = null;
      state.fetchedAt = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEstablishmentsForUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEstablishmentsForUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.establishments = action.payload.establishments ?? [];
        state.generators = action.payload.generators ?? [];
        state.states = action.payload.states ?? [];
        state.fetchedAt = Date.now();
      })
      .addCase(fetchEstablishmentsForUser.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Erro ao carregar filtros';
      })
      .addCase(fetchMaterialsForUser.fulfilled, (state, action) => {
        state.materials = action.payload ?? [];
      });
  },
});

export const { clearEstablishmentAccess } = establishmentAccessSlice.actions;
export default establishmentAccessSlice.reducer;
